import { api } from 'api';
import { CurrencyCodes } from 'common/configs/currency';
import { confirmInDangerousDev } from 'common/utils/dev';
import { requestErrorAction } from 'common/utils/requestErrorHandling';
import { PaginationParams, PaginatedResponse } from '../types';
import {
  Client,
  Deposit,
  ExternalBankAccount,
  InternalBankAccount,
  Wallet,
  WalletBalance,
  Withdrawal,
  XactAccount,
} from './actions';

// #region InternalBankAccounts
export interface InternalBankAccountsParams extends PaginationParams {
  balance?: 0 | 1;
}

export const requestInternalBankAccounts = async (
  params?: InternalBankAccountsParams
) =>
  await api.get<PaginatedResponse<InternalBankAccount>>(
    '/contact/self/xact/account/client/?balance=1',
    {
      params: {
        balance: 1,
        ...(params || {}),
      },
    }
  );

export const doGetInternalBankAccounts =
  (params?: InternalBankAccountsParams) => async (dispatch) => {
    dispatch({ type: 'internal_bank_accounts/started' });

    try {
      const response = await requestInternalBankAccounts(params);

      dispatch({
        type: 'internal_bank_accounts/finished',
        accounts: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'internal_bank_accounts/finished' },
          e,
          `Failed to get internal bank accounts.`
        )
      );
    }
  };
// #endregion

// #region Wallets
export const requestUserWallets = async (
  user: number | string = 'self',
  params?: PaginationParams
) =>
  await api.get<PaginatedResponse<Wallet>>(`/contact/${user}/wallet/`, {
    params,
  });

export const doGetUserWallets =
  (user: number | string = 'self', params?: PaginationParams) =>
  async (dispatch) => {
    dispatch({ type: 'wallets/user/started' });

    try {
      const response = await requestUserWallets(user, params);

      dispatch({
        type: 'wallets/user/finished',
        wallets: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'wallets/user/finished' },
          e,
          `Failed to get user "${user}" wallets.`
        )
      );
    }
  };

export const requestUserWalletBalances = async (
  user: number | string = 'self',
  params?: PaginationParams
) =>
  await api.get<PaginatedResponse<WalletBalance>>(
    `/contact/${user}/wallet/balance/`,
    {
      params,
    }
  );

export const doGetUserWalletBalances =
  (user: number | string = 'self', params?: PaginationParams) =>
  async (dispatch) => {
    dispatch({ type: 'wallets/balances/user/started' });

    try {
      const response = await requestUserWalletBalances(user, params);

      dispatch({
        type: 'wallets/balances/user/finished',
        balances: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'wallets/balances/user/finished' },
          e,
          `Failed to get user "${user}" wallet balances.`
        )
      );
    }
  };

export const doGetUserWalletsWithBalance =
  (user: number | string = 'self', params?: PaginationParams) =>
  async (dispatch) => {
    dispatch({ type: 'wallets_with_balance/user/started' });

    try {
      const [wallets, balances] = await Promise.all([
        requestUserWallets(user, params),
        requestUserWalletBalances(user, params),
      ]);

      dispatch({
        type: 'wallets_with_balance/user/finished',
        wallets: wallets.data.results.map((wallet, index) => ({
          ...balances.data.results[index],
          ...wallet,
        })),
        count: wallets.data.count,
        next: wallets.data.next,
        previous: wallets.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'wallets_with_balance/user/finished' },
          e,
          `Failed to get user "${user}" wallets with balance.`
        )
      );
    }
  };

export interface UpdateWalletCreditLimitBodyData {
  credit: number;
}

export const requestUpdateWalletCreditLimit = async (
  walletId: number,
  data: UpdateWalletCreditLimitBodyData
) => await api.post(`/wallet/${walletId}/credit/`, data);

export const doUpdateWalletCreditLimit =
  (walletId, data: UpdateWalletCreditLimitBodyData) => async (dispatch) => {
    dispatch({ type: 'wallets/credit/update/started' });

    try {
      await requestUpdateWalletCreditLimit(walletId, data);

      dispatch({
        type: 'wallets/credit/update/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'wallets/credit/update/finished', success: false },
          e,
          `Failed to update wallet "${walletId}" credit limit.`
        )
      );
    }
  };

export interface UpdateWalletThresholdBodyData {
  settlement_threshold: number;
}

export const requestUpdateWalletThreshold = async (
  walletId: number,
  data: UpdateWalletThresholdBodyData
) => await api.patch(`/wallet/${walletId}/`, data);

export const doUpdateWalletThreshold =
  (walletId, data: UpdateWalletThresholdBodyData) => async (dispatch) => {
    dispatch({ type: 'wallets/threshold/update/started' });

    try {
      await requestUpdateWalletThreshold(walletId, data);

      dispatch({
        type: 'wallets/threshold/update/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'wallets/threshold/update/finished', success: false },
          e,
          `Failed to update wallet "${walletId}" threshold.`
        )
      );
    }
  };

export interface UpdateWalletMinBalanceBodyData {
  min_balance: number;
}

export const requestUpdateWalletMinBalance = async (
  walletId: number,
  data: UpdateWalletMinBalanceBodyData
) => await api.patch(`/wallet/${walletId}/`, data);

export const doUpdateWalletMinBalance =
  (walletId, data: UpdateWalletMinBalanceBodyData) => async (dispatch) => {
    dispatch({ type: 'wallets/min_balance/update/started' });

    try {
      await requestUpdateWalletMinBalance(walletId, data);

      dispatch({
        type: 'wallets/min_balance/update/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'wallets/min_balance/update/finished', success: false },
          e,
          `Failed to update wallet "${walletId}" minimum balance.`
        )
      );
    }
  };

// #endregion

// #region Clients
export interface ClientsParams extends PaginationParams {
  balance?: number;
}

export const requestUserClients = async (
  user: string | number = 'self',
  params?: ClientsParams
) =>
  await api.get<PaginatedResponse<Client>>(
    `/contact/${user}/xact/account/client/`,
    {
      params: { balance: 1, ...params },
    }
  );

export const doGetUserClients =
  (user: string | number = 'self', params?: ClientsParams) =>
  async (dispatch) => {
    dispatch({ type: 'clients/user/started' });

    try {
      const response = await requestUserClients(user, params);

      dispatch({
        type: 'clients/user/finished',
        clients: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'clients/user/finished' },
          e,
          `Failed to get user "${user}" clients.`
        )
      );
    }
  };
// #endregion

// #region ExternalBankAccounts
export interface ExternalBankAccountsParams extends PaginationParams {
  currency?: CurrencyCodes;
}

export const requestExternalBankAccounts = async (
  params?: ExternalBankAccountsParams
) =>
  await api.get<PaginatedResponse<ExternalBankAccount>>(
    '/contact/agent/bank_account/',
    {
      params: {
        ...(params || {}),
      },
    }
  );

export const doGetExternalBankAccounts =
  (params?: ExternalBankAccountsParams) => async (dispatch) => {
    dispatch({ type: 'external_bank_accounts/started' });

    try {
      const response = await requestExternalBankAccounts(params);

      dispatch({
        type: 'external_bank_accounts/finished',
        accounts: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'external_bank_accounts/finished' },
          e,
          `Failed to get external bank accounts.`
        )
      );
    }
  };
// #endregion

// #region Deposit
export interface DepositBodyData {
  wallet: string;
  destination: string;
  reference: string;
  currency: CurrencyCodes;
  amount: number;
}

export const requestDeposit = async (data: DepositBodyData) =>
  confirmInDangerousDev(
    async () => await api.post<Deposit>('/topup_request/', data)
  );

export const doDeposit = (data: DepositBodyData) => async (dispatch) => {
  dispatch({ type: 'deposit/started' });

  try {
    if (data.amount < 0) data.amount = -data.amount;
    const response = await requestDeposit(data);

    dispatch({
      type: 'deposit/finished',
      deposit: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'deposit/finished' },
        e,
        `Failed to make deposit.`
      )
    );
  }
};
// #endregion

// #region Withdrawal
export interface WithdrawalBodyData {
  wallet: string;
  currency: CurrencyCodes;
  reference: string;
  amount: number;
}

export const requestWithdrawal = async (data: WithdrawalBodyData) =>
  confirmInDangerousDev(
    async () => await api.post<Withdrawal>('/withdraw_request/', data)
  );

export const doWithdrawal = (data: WithdrawalBodyData) => async (dispatch) => {
  dispatch({ type: 'withdrawal/started' });

  try {
    if (data.amount > 0) data.amount = -data.amount;
    const response = await requestWithdrawal(data);

    dispatch({
      type: 'withdrawal/finished',
      withdrawal: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'withdrawal/finished' },
        e,
        `Failed to make withdrawal.`
      )
    );
  }
};
// #endregion

// #region XactAccount
export const requestXactAccount = async (fullCode: string) =>
  confirmInDangerousDev(
    async () => await api.get<XactAccount>(`/xact/account/${fullCode}`)
  );

export const doGetXactAccount = (fullCode: string) => async (dispatch) => {
  dispatch({ type: 'xact/account/started' });

  try {
    const response = await requestXactAccount(fullCode);

    dispatch({
      type: 'xact/account/finished',
      account: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'xact/account/finished' },
        e,
        `Failed to get xact account.`
      )
    );
  }
};

export const requestUserXactAccounts = async (
  user: string | number = 'self',
  params: PaginationParams = {}
) =>
  confirmInDangerousDev(
    async () =>
      await api.get<PaginatedResponse<XactAccount>>(
        `/contact/${user}/xact/account/client`,
        { params }
      )
  );

export const doGetUserXactAccounts =
  (user: string | number = 'self', params: PaginationParams = {}) =>
  async (dispatch) => {
    dispatch({ type: 'xact/user/accounts/started' });

    try {
      const response = await requestUserXactAccounts(user);

      dispatch({
        type: 'xact/user/accounts/finished',
        accounts: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'xact/user/accounts/finished' },
          e,
          `Failed to get user xact accounts.`
        )
      );
    }
  };
// #endregion
